import { apiClient as api } from "./httpClient";

// Agenda (RN-114) — fase de leitura: o backend lê o tenant (atualizado do Bubble
// pelo sync dedicado de 5 min) e devolve datas já no relógio da entidade (RN-305).

export interface ScheduleItem {
  id: string;
  /** ISO local da entidade (sem offset) — ex.: 2026-07-22T14:30:00 */
  dateTimeInitial: string;
  dateTimeFinal: string;
  allDay: boolean;
  patientId: string | null;
  patientName: string | null;
  professionalId: string | null;
  professionalName: string | null;
  serviceTypeId: string | null;
  serviceTypeName: string | null;
  teamId: string | null;
  teamName: string | null;
  statusDisplay: string | null;
  patientPresence: string | null;
  patientConfirmation: string | null;
  reasonToCancellation: string | null;
  reasonToDelete: string | null;
  active: boolean;
  notes: string | null;
  subject: string | null;
  occurrenceNumber: number | null;
  occurrences: number | null;
  eventValueUnit: number | null;
  eventValueUnitDiscount: number | null;
  eventValueTotalUnit: number | null;
  eventValueTotalAll: number | null;
  /** pago (pagamento efetuado) — agendamento pago não pode ser excluído */
  paid: boolean;
  /** data do pagamento (ISO); nula quando não pago */
  paymentDate: string | null;
  /** valor efetivamente pago — alerta de diferença pago × valor vigente (RN-118d.1) */
  paymentFinalValue: number | null;
  /** desconto concedido NO pagamento — o alerta da RN-118d.1 compara o pago
      com (valor vigente − este desconto) */
  paymentDiscount: number | null;
  /** janela de edição vencida (schedule_settings.edit_lock_days): somente leitura */
  locked: boolean;
  /** quantas vezes a data/horário desta ocorrência foi remarcada */
  numReschedule: number;
  /** sala escolhida no agendamento (RN-548); nula = sala padrão do profissional */
  roomId: string | null;
  roomName: string | null;
  /** tamanho do pacote do serviço (RN-228); nulo quando o serviço não é pacote */
  packageSize?: number | null;
  /** serviço de pacote sem série: herança do legado nunca ligada (RN-228) */
  packageUnlinked?: boolean;
}

export interface ScheduleListResult {
  items: ScheduleItem[];
}

export interface ScheduleOptionItem {
  id: string;
  name: string;
  /** Pacote de N sessões (tipos de serviço): > 1 trava a quantidade da recorrência (RN-118j) */
  packageAmount?: number | null;
}

/** Serviço prestado pelo profissional com o valor unitário da sessão (RN-405).
 *  O valor é por serviço E equipe; teamId nulo = linha sem equipe */
export interface ScheduleProfessionalService {
  serviceTypeId: string;
  teamId: string | null;
  value: number | null;
  /** Duração prevista da sessão, em minutos. SUGESTÃO: a tela avisa quando o horário montado
   *  difere dela, mas não impede gravar (decisão do Hilton, 25/09/2026). */
  sessionMinutes: number | null;
}

/** Profissional com vínculos: equipes de que participa e serviços com valor.
 *  active=false: desligado — só aparece com o filtro "mostrar inativos" (histórico) */
export interface ScheduleProfessionalOption {
  id: string;
  name: string;
  professionalTypeId: string | null;
  active: boolean;
  teamIds: string[];
  services: ScheduleProfessionalService[];
  /** sala padrão do profissional hoje (alocação vigente, RN-548) */
  defaultRoomId: string | null;
  defaultRoomName: string | null;
}

/** Equipe com os pacientes atrelados (list_of_patients); vazio = sem restrição.
 *  mainTeam: equipe principal — pré-selecionada no filtro da agenda e no modal */
export interface ScheduleTeamOption {
  id: string;
  name: string;
  mainTeam: boolean;
  patientIds: string[];
}

/** Desconto do paciente (patient_discounts) em R$ por atendimento; critérios nulos = genérico */
export interface SchedulePatientDiscount {
  discount: number;
  serviceTypeId: string | null;
  professionalTypeId: string | null;
  professionalIds: string[];
  teamId: string | null;
  /** vigência (ISO); nulo = sem limite */
  initialDate: string | null;
  finalDate: string | null;
  /** data de cadastro (ISO): entre regras sobrepostas vale a mais recente (RN-116, 05/09/2026) */
  createdAt: string | null;
}

/** Status do catálogo (option set do Bubble) com a cor definida no sistema */
export interface ScheduleStatusOption {
  name: string;
  color: string | null;
  order: number;
  /** confirmação finalizadora (RN-114b): bloqueia presença no agendamento */
  isTerminal?: boolean;
  /** significado para o produto (RN-114d): attended, no_show, confirmed, cancelled_* ou nulo */
  kind?: string | null;
}

export interface ScheduleOptions {
  professionals: ScheduleProfessionalOption[];
  serviceTypes: ScheduleOptionItem[];
  teams: ScheduleTeamOption[];
  /** catálogo ScheduleConfirmation (Confirmado, Cancelado paciente/profissional/...) com cor */
  confirmationStatuses: ScheduleStatusOption[];
  /** catálogo PacientPresence (Compareceu, Não compareceu, ...) com cor */
  presenceStatuses: ScheduleStatusOption[];
  /** salas ativas da entidade (RN-548) */
  rooms: ScheduleOptionItem[];
  /** horário de atendimento da clínica (RN-118o): weekday 0 = domingo; minutos desde a meia-noite */
  businessHours?: { weekday: number; startMinute: number; endMinute: number }[];
  /** escopo do perfil da sessão (RN-523): 'own' pré-seleciona só o próprio profissional */
  dataScope: 'own' | 'entity';
  /** UEPRs do usuário logado (vazio quando os dois escopos são 'entity') */
  ownProfessionalIds: string[];
  /** Escopo de ESCRITA na agenda (RN-115b): 'own' = Profissional e Diretoria só na própria agenda */
  writeScope: 'own' | 'entity';
}

/** Criação pelo modal (fase B, RN-116/RN-302). Valores NÃO vão no payload — o servidor
 *  resolve unitário, desconto e repasse; o que o modal mostra é prévia. */
export interface CreateSchedulePayload {
  patientId: string;
  professionalRegistrationId: string;
  teamId: string | null;
  serviceTypeId: string;
  /** data local da entidade (yyyy-MM-dd) */
  startDate: string;
  /** HH:mm no relógio da entidade */
  startTime: string;
  endTime: string;
  recurring: boolean;
  /** daily | weekly | biweekly | monthly (null quando único) */
  recurrenceType: string | null;
  occurrences: number;
  notes: string | null;
  confirmationStatus: string | null;
  presenceStatus: string | null;
  /** datas removidas na prévia da recorrência (exceções do gerador) */
  skippedDates: string[];
  /** sala do atendimento (RN-548); nula/ausente = sala padrão do profissional */
  roomId?: string | null;
  /** sessões da série montadas na tela (RN-118m); presente, substitui o gerador do servidor */
  slots?: ScheduleSlot[];
}

/** Sessão da série no relógio da entidade: data yyyy-MM-dd e horários HH:mm */
export interface ScheduleSlot {
  date: string;
  startTime: string;
  endTime: string;
}

/** Lista da série editável (RN-118m): remanejamentos e remoções de sessões limpas */
export interface RescheduleSeriesPayload {
  moves: (ScheduleSlot & { scheduleId: string })[];
  deletions: string[];
  deleteReason: string | null;
}

export interface RescheduleSeriesResult {
  moved: number;
  deleted: number;
}

export interface CreateScheduleResult {
  parentId: string;
  ids: string[];
}

/** Edição (fase B): tudo menos o paciente, dentro da janela de edição; applyToSeries
 *  aplica a ESTE E OS SEGUINTES da série (datas assumem o novo horário) */
export interface UpdateSchedulePayload {
  professionalRegistrationId: string;
  teamId: string | null;
  serviceTypeId: string;
  startDate: string;
  startTime: string;
  endTime: string;
  confirmationStatus: string | null;
  presenceStatus: string | null;
  notes: string | null;
  /** motivo do cancelamento — obrigatório ao mudar para "Cancelado *" */
  cancellationReason: string | null;
  applyToSeries: boolean;
  /** sala do atendimento (RN-548): ausente/nula = não mexe; '' limpa e volta à padrão do profissional */
  roomId?: string | null;
}

export interface UpdateScheduleResult {
  updated: number;
}

/** Entrada do histórico (trilha de auditoria traduzida); occurredAt no relógio da entidade */
export interface ScheduleHistoryEntry {
  occurredAt: string;
  user: string | null;
  action: string;
}

/** Exclusão LÓGICA (active=false + motivo); includeFollowing = série deste em diante */
export interface DeleteSchedulePayload {
  reason: string;
  includeFollowing: boolean;
}

export interface DeleteScheduleResult {
  deleted: number;
  skippedProtected: number;
}

/** Ocorrência da série — TODAS, inclusive excluídas (active=false), para a lista contar a
 *  história completa; datas no relógio da entidade. pristine=false ou paid=true =
 *  protegida contra exclusão em série; cancelled = confirmação "Cancelado *" */
export interface ScheduleSeriesItem {
  id: string;
  dateTimeInitial: string;
  dateTimeFinal: string;
  occurrenceNumber: number | null;
  pristine: boolean;
  paid: boolean;
  active: boolean;
  cancelled: boolean;
  /** presença marcada: em série de pacote, o pacote está iniciado (item 191) */
  presenceMarked?: boolean;
  /** fora da janela de edição: só leitura (RN-118k) */
  locked?: boolean;
  /** status exibido da ocorrência e cor do catálogo (RN-228) */
  statusDisplay?: string | null;
  statusColor?: string | null;
  /** presença com significado "atendido" / "falta" (RN-114d) */
  attended?: boolean;
  noShow?: boolean;
}

/** Série com os dados do pacote do serviço (RN-228/RN-227) */
export interface ScheduleSeriesInfo {
  items: ScheduleSeriesItem[];
  packageSize: number | null;
  packageChargeMode: 'total' | 'attended' | null;
}

export interface SchedulesQueryParams {
  /** datas locais da entidade (yyyy-MM-dd), inclusivas */
  from: string;
  to: string;
  /** CSV de ids de UEPR */
  professionals?: string;
  /** CSV de ids de pacientes */
  patients?: string;
  includeDeleted?: boolean;
}

/** Aba Agenda do cadastro do paciente (RN-533): filtros opcionais + paginação no servidor */
export interface PatientSchedulesQueryParams {
  professionalId?: string;
  /** status_display gravado (nomes dos catálogos de confirmação/presença) */
  status?: string;
  /** datas locais da entidade (yyyy-MM-dd), inclusivas */
  from?: string;
  to?: string;
  pageNumber: number;
  pageSize: number;
}

export interface PatientScheduleProfessional {
  id: string;
  name: string;
}

/** Mesmo item da agenda; professionals = quem já atendeu o paciente (independe dos filtros);
 *  totalUntilToday/totalAfterToday = histórico completo partido por hoje (relógio da entidade) */
export interface PatientScheduleListResult {
  items: ScheduleItem[];
  totalCount: number;
  professionals: PatientScheduleProfessional[];
  totalUntilToday: number;
  totalAfterToday: number;
}

// Confirmações cujo significado é cancelamento (cancelled_*), lidas do catálogo uma vez por
// sessão da página: o catálogo quase nunca muda, e o aviso de conflito consulta a cada data
let cancelledNamesCache: Promise<Set<string>> | null = null;
const cancelledConfirmationNames = (): Promise<Set<string>> => {
  if (!cancelledNamesCache) {
    cancelledNamesCache = api.get("/schedules/options")
      .then(r => new Set<string>(((r.data as ScheduleOptions).confirmationStatuses ?? [])
        .filter(s => (s.kind ?? "").startsWith("cancelled"))
        .map(s => s.name)))
      .catch(() => {
        cancelledNamesCache = null;
        return new Set<string>();
      });
  }
  return cancelledNamesCache;
};

export const schedulesService = {
  /** Planilha do recorte que esta na tela (RN-120): mesma janela, mesmos profissionais. */
  exportXlsx: async (params: SchedulesQueryParams): Promise<Blob> => {
    const response = await api.get(`/schedules/export`, {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionals || undefined,
        patients: params.patients || undefined,
      },
      responseType: "blob",
    });
    return response.data as Blob;
  },

  getSchedules: async (params: SchedulesQueryParams): Promise<ScheduleListResult> => {
    const response = await api.get("/schedules", { params });
    return response.data;
  },

  /** Agendamentos que ocupam o horário do profissional OU do paciente no período — base do aviso
   *  de conflito do modal. Cancelado libera o horário, e cancelado é decidido pelo SIGNIFICADO do
   *  status (kind cancelled_*, RN-114d), nunca pelo nome: cada clínica nomeia o seu catálogo.
   *  Consulta em fatias de 60 dias (a API aceita janelas de até 62). */
  findConflicts: async (
    professionalId: string | null, patientId: string | null, from: string, to: string): Promise<ScheduleItem[]> => {
    if ((!professionalId && !patientId) || !from || !to) return [];
    const cancelled = await cancelledConfirmationNames();
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const windows: [string, string][] = [];
    let cursor = new Date(`${from}T00:00:00`);
    const last = new Date(`${to}T00:00:00`);
    while (cursor <= last && windows.length < 24) {
      const end = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 59);
      windows.push([iso(cursor), iso(end < last ? end : last)]);
      cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    }
    const calls: Promise<ScheduleListResult>[] = [];
    windows.forEach(([f, t]) => {
      if (professionalId) calls.push(schedulesService.getSchedules({ from: f, to: t, professionals: professionalId }));
      if (patientId) calls.push(schedulesService.getSchedules({ from: f, to: t, patients: patientId }));
    });
    const byId = new Map<string, ScheduleItem>();
    (await Promise.all(calls)).forEach(r => r.items.forEach(i => byId.set(i.id, i)));
    return Array.from(byId.values()).filter(i => !cancelled.has(i.patientConfirmation ?? ""));
  },

  /** Agenda de um paciente (RN-533): ativos, do mais recente ao mais antigo, paginados */
  getByPatient: async (
    patientId: string, params: PatientSchedulesQueryParams): Promise<PatientScheduleListResult> => {
    const response = await api.get(`/schedules/by-patient/${patientId}`, { params });
    return response.data;
  },

  /** RN-409i: um agendamento pelo id — alimenta o modal de efetivação fora da agenda */
  getById: async (id: string): Promise<ScheduleItem> => {
    const response = await api.get(`/schedules/${id}`);
    return response.data;
  },

  getOptions: async (): Promise<ScheduleOptions> => {
    const response = await api.get("/schedules/options");
    return response.data;
  },

  getPatientDiscounts: async (patientId: string): Promise<SchedulePatientDiscount[]> => {
    const response = await api.get(`/schedules/patient-discounts/${patientId}`);
    return response.data.items;
  },

  /** A série inteira é gravada na chamada (Bubble impõe ~700ms por escrita):
   *  12 ocorrências ≈ 10s — timeout largo de propósito */
  createSchedule: async (payload: CreateSchedulePayload): Promise<CreateScheduleResult> => {
    const response = await api.post("/schedules", payload, { timeout: 120000 });
    return response.data;
  },

  updateSchedule: async (
    id: string, payload: UpdateSchedulePayload): Promise<UpdateScheduleResult> => {
    const response = await api.put(`/schedules/${id}`, payload, { timeout: 120000 });
    return response.data;
  },

  deleteSchedule: async (
    id: string, payload: DeleteSchedulePayload): Promise<DeleteScheduleResult> => {
    const response = await api.delete(`/schedules/${id}`, { data: payload, timeout: 120000 });
    return response.data;
  },

  /** RN-118m: aplica numa operação só as mudanças da lista da série aberta por {id} */
  rescheduleSeries: async (
    id: string, payload: RescheduleSeriesPayload): Promise<RescheduleSeriesResult> => {
    const response = await api.put(`/schedules/${id}/series`, payload, { timeout: 120000 });
    return response.data;
  },

  getSeries: async (id: string): Promise<ScheduleSeriesItem[]> => {
    const response = await api.get(`/schedules/${id}/series`);
    return response.data.items;
  },

  getSeriesInfo: async (id: string): Promise<ScheduleSeriesInfo> => {
    const response = await api.get(`/schedules/${id}/series`);
    return {
      items: response.data.items ?? [],
      packageSize: response.data.packageSize ?? null,
      packageChargeMode: response.data.packageChargeMode ?? null,
    };
  },

  getHistory: async (id: string): Promise<ScheduleHistoryEntry[]> => {
    const response = await api.get(`/schedules/${id}/history`);
    return response.data.items;
  },
};

/** Erros de negócio conhecidos em PT-BR; o code vem do Result.Error do backend.
 *  Compartilhado entre a agenda e a efetivação pela fila de oportunidades (RN-409i). */
export const mapScheduleError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? '';
  const businessMessages: Record<string, string> = {
    'ProfessionalsSchedule.MissingServiceValue':
      'Não há valor de serviço ativo para o profissional, serviço e equipe selecionados.',
    'ProfessionalsSchedule.OwnScopeDenied':
      'Seu perfil só permite mexer na própria agenda, para pacientes vinculados a você.',
    'ProfessionalsSchedule.InvalidTimeRange': 'O horário final deve ser maior que o inicial.',
    'ProfessionalsSchedule.InvalidOccurrences': 'A recorrência deve ter entre 2 e 52 ocorrências.',
    'ProfessionalsSchedule.InvalidRecurrence': 'Escolha a periodicidade da recorrência.',
    'ProfessionalsSchedule.InvalidStatus': 'Status de confirmação ou presença inválido.',
    'ProfessionalsSchedule.ReferenceNotFound': 'Registro selecionado não encontrado na entidade.',
    'ProfessionalsSchedule.NotFound': 'Agendamento não encontrado (pode ter sido excluído).',
    'ProfessionalsSchedule.ReasonRequired': 'Informe o motivo da exclusão.',
    'ProfessionalsSchedule.DeleteBlockedPaid': 'Agendamento pago não pode ser excluído.',
    'ProfessionalsSchedule.PackageRequiresSeries':
      'Este serviço é um pacote: agende como série com a quantidade de sessões do pacote.',
    'ProfessionalsSchedule.PackageSeriesStarted':
      'Pacote já iniciado (com presença marcada): não é possível excluir sessões. Remaneje as que faltam.',
    'ProfessionalsSchedule.PackageDeleteWholeSeries':
      'Pacote ainda não iniciado só pode ser excluído inteiro, nunca uma sessão sozinha.',
    'ProfessionalsSchedule.PackageSeriesHasProtected':
      'O pacote tem sessões pagas ou com status marcado; nada foi excluído. Remaneje as sessões que faltam.',
    'ProfessionalsSchedule.DeleteBlockedStatus':
      'Agendamento com presença ou confirmação marcada não pode ser excluído.',
    'ProfessionalsSchedule.BubbleWriteFailed':
      'Falha ao gravar no sistema legado. Tente novamente em instantes.',
    'ProfessionalsSchedule.Locked':
      'Agendamento fora da janela de edição — somente leitura para preservar o histórico.',
    'ProfessionalsSchedule.RetroactiveLimit':
      'Agendamento retroativo só é permitido dentro da janela de edição.',
    'ProfessionalsSchedule.RequiredFields':
      'Profissional, equipe e serviço são obrigatórios.',
    'ProfessionalsSchedule.PresenceTooEarly':
      'Presença só pode ser marcada a partir do dia do atendimento.',
    'ProfessionalsSchedule.CancellationReasonRequired':
      'Informe o motivo do cancelamento.',
    'ProfessionalsSchedule.InvalidSlots':
      'Há sessões em conflito na lista: confira datas e horários que se sobrepõem ou ficaram antes da primeira sessão.',
    'ProfessionalsSchedule.SeriesItemNotEditable':
      'Uma das sessões já foi paga, tem status marcado ou saiu da janela de edição. Abra a sessão para alterá-la.',
    'ProfessionalsSchedule.PackageSessionRemoval':
      'Sessões de pacote não podem ser removidas pela lista; só a data e o horário podem mudar.',
  };
  return businessMessages[code] || 'Não foi possível concluir a operação. Tente novamente.';
};
